// src/pages/ClassDetailPage.jsx
// 授業詳細ページ【2026-09-04 追加 / 09-04 サブページ分割】
//
// /classes/:classId/:section  （section = schedule | summary、既定は summary）
// 【2026-09-17】「受講者名簿」は学生管理に統合した。/classes/:id/roster は
//   /students?classId=:id へ転送する（古いリンク・ブックマーク用）。
//
// 画面上部の授業名はセレクトで、選ぶとそのまま別の授業の同じページへ移動する。
// 左のサブメニューで「授業スケジュール」と「出席記録」を切り替える。
// URLが分かれているので、ブラウザの戻る／進むもブックマークも効く。
//
// ビーコンは教員ごとの固定割り当てなので、ここでは扱わない。

import { STATUS } from '../components/StudentStatusBadges';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link, NavLink, Navigate } from 'react-router-dom';
import ClassSummaryPane  from '../components/ClassSummaryPane';
import SessionMergePane  from '../components/SessionMergePane';
import SchedulePane      from '../components/SchedulePane';
import AttendanceEditPane from '../components/AttendanceEditPane';
import AttendanceRequests from './AttendanceRequests';
import ReportExportPane  from '../components/ReportExportPane';
import AttendanceCsvPane from '../components/AttendanceCsvPane';
import AssessmentImportPane from '../components/AssessmentImportPane';
import AllClassesSummaryPane from '../components/AllClassesSummaryPane';
import AllClassesSchedulePane from '../components/AllClassesSchedulePane';
import PeriodBar, { usePeriod } from '../components/PeriodBar';



// 授業に紐づく操作はすべてここに集約する【2026-09-04】
//   もともと独立ページだった「出席記録」「レポート出力」「CSVインポート」は、
//   どれも授業を選んでから使う画面だったので、1つの「出席記録」にまとめた。
//   授業の選択は上部の授業名セレクトが一括で担う。
const SECTIONS = [
  { key: 'schedule', label: '授業スケジュール' },
  { key: 'summary',  label: '出席記録' },
  // 先生が出欠を直す専用のページ【2026-10-07】（受講者名簿は先生のメニューから届かないため）
  //   学生からの申請・確認待ちの打刻（もとの「出欠の確認」）も同じページにまとめた
  { key: 'edit',     label: '出欠の確認・修正' },
];

// 旧URL（分かれていた頃のリンク）は出席記録へ寄せる
const LEGACY_SECTIONS = { export: 'summary', import: 'summary' };

export default function ClassDetailPage() {
  const { classId, section } = useParams();
  const navigate = useNavigate();
  // 「すべての授業」を選んでいる状態。出席記録と授業スケジュール【2026-10-05】が使える【2026-09-04】
  const isAll = String(classId) === 'all';
  const resolved = SECTIONS.some(s => s.key === section)
    ? section
    : (LEGACY_SECTIONS[section] || 'summary');
  // 回をまとめる画面は出席記録の下の画面。タブには出さない【2026-09-28】
  const current = isAll ? (['schedule', 'edit'].includes(resolved) ? resolved : 'summary') : (section === 'merge' ? 'merge' : resolved);

  // 直前に開いた授業（「すべての授業」からスケジュール等へ戻る先）
  let lastClassId = null;
  try { lastClassId = localStorage.getItem('lastClassId'); } catch (e) { /* 参照できなくても動く */ }

  const [allClasses, setClasses] = useState([]);   // 授業切り替え用（年度ぶん）
  // 授業名セレクトには「今の期間」の授業だけ出す【2026-09-17】
  const period  = usePeriod();
  const classes = useMemo(() => period.filter(allClasses), [period.filter, allClasses]); // eslint-disable-line react-hooks/exhaustive-deps
  const [matrix, setMatrix]       = useState(null); // { class, sessions, students, heldSessions }
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [err, setErr]             = useState('');
  const [msg, setMsg]             = useState('');

  const token = () => localStorage.getItem('token');
  const auth  = () => ({ Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' });

  const loadClasses = async () => {
    try {
      const q = period.year ? '?academicYear=' + period.year : '';
      const res = await fetch('/api/schedule/classes/overview' + q, { headers: auth() });
      const d   = await res.json();
      setClasses(d.classes || []);
    } catch (e) { /* 切り替え用なので失敗しても本体は動く */ }
  };

  const loadAll = async () => {
    // 全授業モードでは1つの授業のデータを引く必要がない
    if (isAll) { setMatrix(null); setSchedules([]); setLoading(false); return; }
    setLoading(true); setErr('');
    try {
      const [mR, sR] = await Promise.all([
        fetch('/api/schedule/classes/' + classId + '/attendance-matrix', { headers: auth() }),
        fetch('/api/schedule/classes/' + classId + '/schedules',          { headers: auth() }),
      ]);
      const md = await mR.json();
      const sd = await sR.json();
      if (!md.success) throw new Error(md.message || '授業の取得に失敗しました');
      setMatrix(md);
      setSchedules(sd.schedules || sd.data || []);
    } catch (e) { setErr(e.message); }
    finally     { setLoading(false); }
  };

  useEffect(() => { if (period.ready) loadClasses(); }, [period.ready, period.year]); // eslint-disable-line

  // 直前に開いた授業を覚えておく。サイドバーのサブメニューの行き先に使う【2026-09-07】
  useEffect(() => {
    if (isAll) return;
    try { localStorage.setItem('lastClassId', String(classId)); } catch (e) { /* 保存できなくても動く */ }
  }, [classId, isAll]);
  useEffect(() => { loadAll(); setMsg(''); }, [classId]); // eslint-disable-line

  const cls          = matrix && matrix.class;
  const sessions     = (matrix && matrix.sessions) || [];
  const students     = (matrix && matrix.students) || [];
  const held         = (matrix && matrix.heldSessions) || 0;
  const placeholders = students.filter(s => s.is_placeholder).length;
  const outsiders    = students.filter(s => s.is_outside_roster).length;

  const shared = { classId, auth, token, reload: loadAll, setErr, setMsg,
                   sessions, students, held, schedules, classes, cls };

  // 受講者名簿は学生管理へ移した【2026-09-17】
  if (section === 'roster' && !isAll) {
    return <Navigate to={'/students?classId=' + classId} replace />;
  }

  return (
    <div className="page-container page-wide">
      {/* ヘッダ：授業名セレクトで別の授業へ移動できる */}
      <div className="page-header">
        <div style={{ fontSize: '0.85rem', marginBottom: '0.4rem' }}>
          <Link to="/classes">← 授業一覧</Link>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>

          <select
            value={classId}
            onChange={e => navigate('/classes/' + e.target.value + '/' +
                                    (e.target.value === 'all' ? (['schedule', 'edit'].includes(current) ? current : 'summary') : current))}
            style={{ fontSize: '1.05rem', fontWeight: 700, padding: '0.3rem 0.5rem', maxWidth: '100%' }}
          >
            <option value="all">— すべての授業 —</option>
            {!isAll && cls && !classes.some(c => String(c.id) === String(classId)) && (
              <option value={classId}>{cls.name}（ほかの期間）</option>
            )}
            {classes.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}{c.semester ? '（' + c.semester + '）' : ''}
              </option>
            ))}
          </select>
        </div>
        <PeriodBar period={period} hiddenCount={allClasses.length - classes.length} />
        {!isAll && cls && cls.closure_id && (
          <p style={{ margin: '0.2rem 0', color: '#1d4ed8', fontSize: '0.85rem' }}>
            この授業は年度の締めが済んでいるため、名簿・出席は見るだけです。
          </p>
        )}
        {isAll && (
          <p style={{ marginTop: '0.4rem' }}>
            上の期間の担当授業をまとめて表示しています。
            個別の授業を見るには上のセレクトで選んでください。
          </p>
        )}
        {!isAll && cls && (
          <p style={{ marginTop: '0.4rem' }}>
            {cls.semester || '学期未設定'}
            {cls.teacher_name && <> ・ 担当 {cls.teacher_name}</>}
            　履修 <strong>{students.length - outsiders}</strong>人
            {placeholders > 0 && (
              <span style={{ color: STATUS.noAccount.color }} title={STATUS.noAccount.title}>
                （うち{STATUS.noAccount.label} {placeholders}）
              </span>
            )}
            {outsiders > 0 && (
              <span style={{ color: STATUS.notOnRoster.color }} title={STATUS.notOnRoster.title}>
                ＋{STATUS.notOnRoster.label} {outsiders}人
              </span>
            )}
            　実施 <strong>{held}</strong>回
          </p>
        )}
      </div>

      {/* ほかの授業にまとめた授業【2026-09-17】 */}
      {!isAll && cls && cls.linked_to_class_id && (
        <div className="card" style={{ borderLeft: '4px solid #2563eb' }}>
          <p style={{ margin: 0 }}>
            この授業は「{cls.linked_to_name}」にまとめています。履修者・出席・集計はまとめ先にあります。{' '}
            <Link to={'/classes/' + cls.linked_to_class_id + '/summary'}>まとめ先の出席記録を開く →</Link>
          </p>
        </div>
      )}
      {!isAll && matrix && (matrix.linkedChildren || []).length > 0 && (
        <div className="card" style={{ borderLeft: '4px solid #2563eb', padding: '0.6rem 1rem' }}>
          <p style={{ margin: 0, fontSize: '0.88rem' }}>
            別学部の同じ授業 {matrix.linkedChildren.map(k => '「' + k.name + '」').join('')} をこの授業にまとめて集計しています。
          </p>
        </div>
      )}
      {err && <div className="card" style={{ borderLeft: '4px solid #ef4444' }}><p style={{ color: '#c62828' }}>{err}</p></div>}
      {msg && <div className="card" style={{ borderLeft: '4px solid #22c55e' }}><p style={{ color: '#166534' }}>{msg}</p></div>}

      {/* サブページの切り替えは左サイドバー（授業管理の下）に出る。
          スマホではサイドバーがドロワーに隠れるので、ここにも横並びで出す。 */}
      <div className="class-subnav-mobile">
        {SECTIONS.map(sec => {
          // 「すべての授業」でも出席記録・授業スケジュールはそのまま「すべての授業」で開く【2026-10-05】
          const id = (!isAll || sec.key === 'summary' || sec.key === 'schedule' || sec.key === 'edit') ? classId : (lastClassId || null);
          return (
            <NavLink
              key={sec.key}
              to={id ? '/classes/' + id + '/' + sec.key : '/classes?section=' + sec.key}
              className={({ isActive }) =>
                'class-subnav-chip' + (isActive || sec.key === current ? ' active' : '')}
            >
              {sec.label}
            </NavLink>
          );
        })}
      </div>

      {loading ? (
        <div className="card"><p>読み込み中...</p></div>
      ) : current === 'schedule' ? (
        isAll
          ? <AllClassesSchedulePane classes={classes} auth={auth} setErr={setErr} setMsg={setMsg} />
          : <SchedulePane {...shared} />
      ) : current === 'edit' ? (
        isAll ? (
          // すべての授業：申請と確認待ちの打刻の一覧（表で直すときは授業を選ぶ）【2026-10-07】
          <div className="card">
            <p style={{ marginTop: 0, fontSize: '0.88rem', color: '#4b5563' }}>
              上の期間に限らず、担当の授業すべての「学生からの申請」と「確認待ちの打刻」です。
              学生×回の表で出欠を直すときは、上のセレクトで授業を選んでください。
            </p>
            <AttendanceRequests embedded />
          </div>
        ) : <AttendanceEditPane {...shared} />
      ) : current === 'merge' ? (
        <SessionMergePane classId={classId} className={cls && cls.name} setErr={setErr} setMsg={setMsg} />
      ) : (
        // 集計・レポート出力・出席CSV取り込みを1ページにまとめて出す。
        // 全授業モードでは授業単位の取り込みは意味がないので出さない。
        <>
          {/* 並び順【2026-09-11】
              全体集計 → レポート出力(プレビュー) → CSV取り込み → 回ごとのサマリ。
              この画面の主役はプレビューなので、回ごとのサマリは一番下に回す。
              ClassSummaryPane が children を受け取って間に挟む形にしてあるのは、
              集計APIの取得を1回で済ませるため（ペインを割ると2回叩くことになる）。 */}
          {isAll ? (
            <>
              <AllClassesSummaryPane setErr={setErr} periodQuery={period.query} />
              <div id="report-export">
                <ReportExportPane classId={classId} classes={classes} periodQuery={period.query}
                                  setErr={setErr} setMsg={setMsg} />
              </div>
            </>
          ) : (
            <ClassSummaryPane classId={classId} setErr={setErr} setMsg={setMsg}>
              {/* 集計カードの「書き出しへ ↓」からここへスクロールする */}
              <div id="report-export">
                <ReportExportPane classId={classId} classes={classes}
                                  setErr={setErr} setMsg={setMsg} />
              </div>
              {/* manaba の小テスト・課題。画面のどこにドロップしても取り込める */}
              <AssessmentImportPane classId={classId} className={cls && cls.name}
                                    setErr={setErr} setMsg={setMsg} />
              <AttendanceCsvPane classId={classId} className={cls && cls.name}
                                 setErr={setErr} setMsg={setMsg} />
            </ClassSummaryPane>
          )}
        </>
      )}
    </div>
  );
}
